import type { BridgeOptionViewModel } from './types'
import { Button } from '@sunwebgroup/uds-ui/button'
import { Checkbox } from '@sunwebgroup/uds-ui/checkbox'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Sheet } from '@sunwebgroup/uds-ui/sheet'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useIntl } from '@/intl'
import styles from './BridgeOptionRow.module.css'

export interface BridgeOptionSheetProps {
  option: BridgeOptionViewModel
  isOpen: boolean
  onSave: (participantIds: string[]) => void
  onCancel: () => void
}

export function BridgeOptionSheet({ option, isOpen, onSave, onCancel }: BridgeOptionSheetProps) {
  const intl = useIntl()
  const [draftParticipantIds, setDraftParticipantIds] = React.useState<string[]>(
    option.assignedParticipantIds,
  )

  React.useEffect(() => {
    if (isOpen) {
      setDraftParticipantIds(option.assignedParticipantIds)
    }
  }, [isOpen, option.assignedParticipantIds])

  const handleParticipantToggle = React.useCallback((participantId: string, checked: boolean) => {
    setDraftParticipantIds(prev =>
      checked ? [...prev, participantId] : prev.filter(id => id !== participantId),
    )
  }, [])

  return (
    <Sheet.Root open={isOpen} onOpenChange={open => !open && onCancel()}>
      <Sheet.Body side="right">
        <Sheet.Header
          titleLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_title' })}
          closeLabel={intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_cancel_button' })}
        />
        <Sheet.Content className={styles.sheetContent}>
          <div className={styles.editPanel}>
            <Typography variant="display-h-regular">{option.label}</Typography>
            <fieldset className={styles.participantFieldset}>
              <legend className={styles.participantLegend}>
                <Typography variant="body-m-regular-snug" as="span">
                  {intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_participants_label' })}
                </Typography>
              </legend>
              <div className={styles.participantList}>
                {option.availableParticipants.map((participant) => {
                  const assignment = option.priceAssignments.find(a =>
                    a.participantIds.includes(participant.id),
                  )
                  const participantPrice = assignment?.price ?? option.price
                  const isChecked = draftParticipantIds.includes(participant.id)
                  const checkboxId = `bridge-participant-${option.id}-${participant.id}`

                  return (
                    <ListItem
                      key={participant.id}
                      as="label"
                      htmlFor={checkboxId}
                      variant="outlined"
                      className={styles.participantItem}
                    >
                      <Checkbox
                        id={checkboxId}
                        checked={isChecked}
                        onCheckedChange={checked => handleParticipantToggle(participant.id, Boolean(checked))}
                      />
                      <Typography variant="body-m-regular-tight" className={styles.participantName}>
                        {participant.name}
                      </Typography>
                      {participantPrice > 0 && (
                        <Typography variant="body-s-regular-tight" className={styles.participantPrice}>
                          {intl.formatCurrency(participantPrice, { showSign: true })}
                        </Typography>
                      )}
                    </ListItem>
                  )
                })}
              </div>
            </fieldset>
          </div>
        </Sheet.Content>
        <Sheet.Footer>
          <Button variant="primary" onClick={() => onSave(draftParticipantIds)} fullWidth>
            {intl.formatMessage({ id: 'checkout_steps_ancillaries_bridge_save_button' })}
          </Button>
        </Sheet.Footer>
      </Sheet.Body>
    </Sheet.Root>
  )
}
